import React, { useState, useEffect, useMemo, useRef } from 'react';
import logoAndes from '../assets/logoAndesBlanco.png';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const normalizeText = (text) => String(text || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const wait = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const fetchCatalogOptions = async (endpoint, fallbackMessage, attempts = 3) => {
  let lastError = null;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`);
      let data;

      try {
        data = await response.json();
      } catch (error) {
        throw new Error(fallbackMessage);
      }

      if (!response.ok || data.error) {
        throw new Error(data.msg || fallbackMessage);
      }

      return Array.isArray(data.data) ? data.data : [];
    } catch (error) {
      lastError = error;

      if (attempt < attempts) {
        await wait(500 * attempt);
      }
    }
  }

  throw lastError || new Error(fallbackMessage);
};

const SearchableSelect = ({
  id,
  name,
  value,
  options,
  placeholder = 'Seleccione',
  searchPlaceholder = 'Buscar...',
  disabled = false,
  loading = false,
  showSearch = true,
  loadingText = 'Cargando...',
  noResultsText = 'No se encontraron resultados',
  onValueChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const safeOptions = Array.isArray(options) ? options : [];
    const query = normalizeText(searchTerm).trim();
    if (!query) {
      return safeOptions;
    }

    return safeOptions.filter((item) => normalizeText(item).includes(query));
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const openDropdown = () => {
    if (disabled || loading) {
      return;
    }

    setIsOpen(true);
  };

  const selectOption = (option) => {
    onValueChange(name, option);
    setIsOpen(false);
  };

  const buttonLabel = loading ? loadingText : value || placeholder;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        id={id}
        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm text-left focus:outline-none focus:ring-2 focus:ring-custom-blue transition disabled:opacity-60 disabled:cursor-not-allowed"
        onClick={openDropdown}
        disabled={disabled || loading}
      >
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>{buttonLabel}</span>
      </button>

      {isOpen && (
        <div className="absolute z-40 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-custom-blue transition"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <ul className="max-h-56 overflow-y-auto py-1">
            {filteredOptions.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">{noResultsText}</li>
            )}
            {filteredOptions.map((item, idx) => (
              <li key={`${item}-${idx}`}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-custom-green-50 transition ${item === value ? 'bg-custom-green-100 text-custom-green-800 font-semibold' : 'text-gray-700'}`}
                  onClick={() => selectOption(item)}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const TicketNewForm = ({ onSubmit = () => {}, onClose = () => {}, isModal = false }) => {
  const [formData, setFormData] = useState({
    requester: '',
    puesto: '',
    sede: '',
    campania: '',
    requiereAutorizacion: '',
    email: '',
    descripcion: '',
    adjuntos: null,
  });

  const [solicitantes, setSolicitantes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [campanias, setCampanias] = useState([]);
  const [requesterError, setRequesterError] = useState(false);
  const [sedeError, setSedeError] = useState(false);
  const [campaignError, setCampaignError] = useState(false);
  const [isLoadingSolicitantes, setIsLoadingSolicitantes] = useState(false);
  const [isLoadingSedes, setIsLoadingSedes] = useState(false);
  const [isLoadingCampanias, setIsLoadingCampanias] = useState(false);

  useEffect(() => {
    const fetchSolicitantes = async () => {
      setIsLoadingSolicitantes(true);
      setRequesterError(false);

      try {
        const data = await fetchCatalogOptions(
          '/webhook/solicitantes',
          'El servidor no devolvio una respuesta valida para solicitantes.'
        );
        setSolicitantes(data);
      } catch (error) {
        setSolicitantes([]);
        setRequesterError(true);
      } finally {
        setIsLoadingSolicitantes(false);
      }
    };

    const fetchSedes = async () => {
      setIsLoadingSedes(true);
      setSedeError(false);

      try {
        const data = await fetchCatalogOptions(
          '/webhook/sedes',
          'El servidor no devolvio una respuesta valida para sedes.'
        );
        setSedes(data);
      } catch (error) {
        setSedes([]);
        setSedeError(true);
      } finally {
        setIsLoadingSedes(false);
      }
    };

    const fetchCampanias = async () => {
      setIsLoadingCampanias(true);
      setCampaignError(false);

      try {
        const data = await fetchCatalogOptions(
          '/webhook/campanias',
          'El servidor no devolvio una respuesta valida para campanias.'
        );
        setCampanias(data);
      } catch (error) {
        setCampanias([]);
        setCampaignError(true);
      } finally {
        setIsLoadingCampanias(false);
      }
    };

    fetchSolicitantes();
    fetchSedes();
    fetchCampanias();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'file' ? files : value,
    }));
  };

  const handleSelectValueChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [serverMsg, setServerMsg] = useState(null);
  const [serverError, setServerError] = useState(false);

  const MAX_TOTAL_SIZE_MB = 500;
  const MAX_FILE_SIZE_MB = 100;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerMsg(null);
    setServerError(false);
    setIsSubmitting(true);

    if (!formData.requester || !formData.puesto || !formData.sede || !formData.campania || !formData.requiereAutorizacion || !formData.descripcion) {
      setServerMsg('Debe completar todos los campos obligatorios.');
      setServerError(true);
      setIsSubmitting(false);
      return;
    }

    const allowedExtensions = [
      '.xlsx', '.csv', '.pdf', '.jpg', '.png', '.docx', '.mp4', '.mp3', '.wmv', '.wma', '.wap', '.doc', '.jpeg', '.avi', '.bmp', '.bpm', '.mov', '.tiff'
    ];

    if (formData.adjuntos && formData.adjuntos.length) {
      for (const file of Array.from(formData.adjuntos)) {
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedExtensions.includes(ext)) {
          setServerMsg(`El archivo "${file.name}" tiene un formato no permitido. Solo se aceptan: ${allowedExtensions.join(', ')}`);
          setServerError(true);
          setIsSubmitting(false);
          return;
        }
      }
    }

    let totalSize = 0;
    if (formData.adjuntos && formData.adjuntos.length) {
      for (const file of Array.from(formData.adjuntos)) {
        totalSize += file.size;
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          setServerMsg(`El archivo "${file.name}" supera el tamano maximo permitido de ${MAX_FILE_SIZE_MB} MB.`);
          setServerError(true);
          setIsSubmitting(false);
          return;
        }
      }
    }

    if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
      setServerMsg(`El tamano total de los archivos adjuntos no debe superar ${MAX_TOTAL_SIZE_MB} MB.`);
      setServerError(true);
      setIsSubmitting(false);
      return;
    }

    const formToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'adjuntos' && value && value.length) {
        for (const file of Array.from(value)) {
          formToSend.append('adjuntos', file);
        }
      } else if (value !== null && value !== undefined && key !== 'adjuntos') {
        formToSend.append(key, value);
      }
    });

    try {
      const response = await fetch(`${API_BASE_URL}/webhook/casos_crm_soporte`, {
        method: 'POST',
        body: formToSend,
      });

      let data;
      try {
        data = await response.json();
      } catch (err) {
        setServerMsg('El servidor no devolvio una respuesta valida.');
        setServerError(true);
        setIsSubmitting(false);
        return;
      }

      setServerMsg(data.msg || 'Respuesta desconocida del servidor');
      setServerError(!!data.error);

      if (!data.error) {
        onSubmit(formData);
        setFormData({
          requester: '',
          puesto: '',
          sede: '',
          campania: '',
          requiereAutorizacion: '',
          email: '',
          descripcion: '',
          adjuntos: null,
        });

        if (isModal) {
          onClose();
        }
      }
    } catch (error) {
      setServerMsg('Error al enviar el formulario. Intente nuevamente.');
      setServerError(true);
    }

    setIsSubmitting(false);
  };

  const [showPopup, setShowPopup] = useState(false);
  const [popupMsg, setPopupMsg] = useState('');
  const [popupIsError, setPopupIsError] = useState(false);

  const catalogErrorMsg = requesterError
    ? 'No se pudo cargar la lista de solicitantes. Puede continuar si el formulario ya tiene la informacion necesaria.'
    : sedeError
      ? 'No se pudo cargar la lista de sedes. Puede continuar si el formulario ya tiene la informacion necesaria.'
      : campaignError
        ? 'No se pudo cargar la lista de campanias. Puede continuar si el formulario ya tiene la informacion necesaria.'
        : '';

  useEffect(() => {
    if (serverMsg) {
      setPopupMsg(serverMsg);
      setPopupIsError(serverError);
      setShowPopup(true);
    }
  }, [serverError, serverMsg]);

  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupMsg('');
    setPopupIsError(false);
    setServerMsg(null);
    setServerError(false);
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-8">
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className={`bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full flex flex-col items-center border-4 ${popupIsError ? 'border-red-300' : 'border-green-300'} animate-fade-in`}>
            <span className="text-5xl mb-2">{popupIsError ? 'X' : 'OK'}</span>
            {popupIsError ? (
              <svg className="w-12 h-12 text-red-500 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-12 h-12 text-green-500 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
            <div className={`text-lg font-semibold text-center mb-6 ${popupIsError ? 'text-red-700' : 'text-green-700'}`}>{popupMsg}</div>
            <button
              type="button"
              onClick={handleClosePopup}
              className={`px-6 py-2 ${popupIsError ? 'bg-custom-green-500' : 'bg-green-500'} text-white rounded-lg font-bold shadow hover:bg-custom-green-700 transition-colors`}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {catalogErrorMsg && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          {catalogErrorMsg}
        </div>
      )}

      <div className="flex flex-col md:flex-row items-center md:items-start justify-center mb-8 gap-6 md:gap-10 max-w-3xl mx-auto">
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-3xl font-extrabold text-custom-green-700 mb-1 tracking-tight flex items-center gap-2 justify-center md:justify-start">
            <span>Solicitudes e Incidentes - TI AndesBPO</span> <span className="text-2xl"></span>
          </h1>
          <p className="text-gray-500 max-w-xl md:max-w-lg">Por favor diligencia el siguiente formulario para que nuestro equipo de soporte pueda ayudarte lo mas pronto posible. Gracias por confiar en nuestro equipo <span className="font-bold text-custom-green-600">TI AndesBPO</span>.</p>
        </div>
        <div className="flex-shrink-0 flex items-center justify-center md:ml-4 md:mr-2 mt-4 md:mt-0">
          <div className="rounded-2xl bg-custom-green-700 px-8 py-6 shadow-xl flex items-center justify-center" style={{ minWidth: 180, minHeight: 100 }}>
            <img src={logoAndes} alt="Logo AndesBPO" className="h-20 w-auto object-contain" style={{ maxWidth: 180 }} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="requester" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
            <span>* Nombre del Solicitante</span>
          </label>
          <SearchableSelect
            id="requester"
            name="requester"
            value={formData.requester}
            options={solicitantes}
            placeholder="Seleccione"
            searchPlaceholder="Buscar solicitante..."
            loading={isLoadingSolicitantes}
            loadingText="Cargando solicitantes..."
            onValueChange={handleSelectValueChange}
          />
        </div>
        <div>
          <label htmlFor="puesto" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
            <span>* Numero del Puesto</span>
          </label>
          <input
            type="text"
            id="puesto"
            name="puesto"
            className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-custom-blue transition"
            placeholder="Ej: 35"
            value={formData.puesto}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d{1,3}$/.test(value)) {
                setFormData((prev) => ({ ...prev, puesto: value }));
              }
            }}
            maxLength={3}
            inputMode="numeric"
            pattern="^\d{1,3}$"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="sede" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
            <span>* Sede</span>
          </label>
          <SearchableSelect
            id="sede"
            name="sede"
            value={formData.sede}
            options={sedes}
            placeholder="Seleccione"
            loading={isLoadingSedes}
            loadingText="Cargando sedes..."
            showSearch={false}
            onValueChange={handleSelectValueChange}
          />
        </div>
        <div>
          <label htmlFor="campania" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
            <span>* Campaña o Área</span>
          </label>
          <SearchableSelect
            id="campania"
            name="campania"
            value={formData.campania}
            options={campanias}
            placeholder="Seleccione"
            searchPlaceholder="Buscar campaña o área..."
            loading={isLoadingCampanias}
            loadingText="Cargando campañas..."
            onValueChange={handleSelectValueChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label htmlFor="requiereAutorizacion" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
            <span>* Requiere Autorización</span>
          </label>
          <SearchableSelect
            id="requiereAutorizacion"
            name="requiereAutorizacion"
            value={formData.requiereAutorizacion}
            options={['Sí', 'No']}
            placeholder="Seleccione"
            showSearch={false}
            onValueChange={handleSelectValueChange}
          />
        </div>
      </div>

      <div>
        <label htmlFor="descripcion" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
          <span>Descripcion</span>
        </label>
        <textarea
          id="descripcion"
          name="descripcion"
          rows="4"
          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-custom-blue transition resize-none"
          placeholder="Describe el error, problema o solicitud..."
          value={formData.descripcion}
          onChange={handleChange}
          required
        ></textarea>
      </div>

      <div>
        <label htmlFor="adjuntos" className="block text-custom-green-700 text-base font-semibold mb-2 flex items-center gap-1">
          <span>Adjuntos</span>
          <span className="text-xs text-gray-400 ml-2">(puede subir archivos relacionados con el problema o la solicitud)</span>
        </label>
        <input
          type="file"
          id="adjuntos"
          name="adjuntos"
          multiple
          accept=".xlsx,.csv,.pdf,.jpg,.png,.docx,.mp4,.mp3,.wmv,.wma,.wap,.doc,.jpeg,.avi,.bmp,.bpm,.mov,.tiff"
          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-custom-blue transition"
          onChange={handleChange}
        />
      </div>

      <div className="flex justify-end space-x-4">
        {isModal && (
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          className={`px-6 py-3 bg-custom-green-500 text-white font-semibold rounded-lg shadow-md transition-colors duration-300 ${isSubmitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-custom-green-700'}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Enviando...' : 'Enviar Ticket'}
        </button>
      </div>
    </form>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div
          className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-3xl transform transition-all duration-300 scale-100 opacity-100 overflow-y-auto"
          style={{ maxHeight: '90vh' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Crear Nuevo Ticket</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto">
        {formContent}
      </div>
    </div>
  );
};

export default TicketNewForm;
